<template>
  <template v-if="aiPanelEnabled">
    <!-- Toggle Button (only visible when panel is closed) -->
    <div v-if="!aiStore.isSidePanelOpen" class="ai-side-panel-toggle">
      <v-btn
        class="toggle-btn"
        size="small"
        elevation="2"
        data-id-e2e="aiPanelToggleBtn"
        @click="togglePanel"
      >
        <v-icon size="18" color="white">mdi-robot-outline</v-icon>
        <span class="toggle-text">
          {{ t('aiAssistant.toggle') }}
        </span>
      </v-btn>
    </div>

    <!-- Navigation Drawer - handles layout integration, footer/navbar boundaries, and animation -->
    <v-navigation-drawer
      :model-value="aiStore.isSidePanelOpen"
      location="right"
      :temporary="!aiStore.isSidePanelPinned"
      :mobile-breakpoint="1000"
      :width="aiStore.sidePanelWidth"
      :scrim="false"
      :persistent="true"
      data-id-e2e="aiSidePanel"
      @update:model-value="handleDrawerUpdate"
    >
      <!-- Resize handle on left edge -->
      <div class="resize-handle" @mousedown="startResize" />

      <!-- Panel Content -->
      <div class="ai-side-panel-content">
        <!-- Panel Header -->
        <div class="ai-side-panel-header">
          <div class="header-content">
            <v-icon class="header-icon" color="primary">mdi-robot-outline</v-icon>
            <h3 class="header-title">{{ t('aiAssistant.name') }}</h3>
            <div class="header-tags">
              <AiTag />
              <BetaTag />
            </div>
          </div>
          <div class="header-actions">
            <v-tooltip location="bottom" open-delay="500">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="text"
                  :icon="aiStore.isSidePanelPinned ? 'mdi-pin' : 'mdi-pin-outline'"
                  size="small"
                  :class="['pin-btn', { 'pin-btn--active': aiStore.isSidePanelPinned }]"
                  data-id-e2e="aiPanelPinBtn"
                  @click="togglePin"
                />
              </template>
              <span>{{
                aiStore.isSidePanelPinned
                  ? t('aiAssistant.panel.unpin')
                  : t('aiAssistant.panel.pin')
              }}</span>
            </v-tooltip>
            <v-btn
              variant="text"
              icon="mdi-close"
              size="small"
              class="close-btn"
              data-id-e2e="aiPanelCloseBtn"
              @click="closePanel"
            />
          </div>
        </div>

        <!-- Mode Tabs (placeholder for future multi-mode support) -->

        <!-- Panel Body -->
        <div class="ai-side-panel-body">
          <!-- Show terms acceptance screen until the user has agreed -->
          <AiTermsAcceptance v-if="!contextStore.hasAcceptedAiTerms" />

          <!-- Conversation Mode -->
          <div v-else class="mode-content">
            <AiConversationPanel :context="aiStore.sidePanelContext" />
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </template>
</template>

<script setup>
import { watchEffect, onMounted, onUnmounted } from 'vue';
import { useAiStore } from '@/stores/ai';
import { useContextStore } from '@/stores/context';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import { useAiRouteContextReset } from '@/composables/use-ai-side-panel';
import AiConversationPanel from '@/components/AiConversationPanel.vue';
import AiTermsAcceptance from '@/components/AiTermsAcceptance.vue';
import AiTag from '@/components/AiTag.vue';
import BetaTag from '@/components/BetaTag.vue';

const MIN_PANEL_WIDTH = 350;
const MAX_PANEL_WIDTH = 600;

// Stores and composables
const aiStore = useAiStore();
const contextStore = useContextStore();
const { t } = useInternationalization();
const { aiPanelEnabled } = useFeature();

// Reset page context on every route change (called once here since AiSidePanel is always mounted)
useAiRouteContextReset();

// Methods
function togglePanel() {
  aiStore.toggleSidePanel();
}

function closePanel() {
  aiStore.closeSidePanel();
}

function togglePin() {
  aiStore.setPinned(!aiStore.isSidePanelPinned);
}

function handleDrawerUpdate(val) {
  if (!val) closePanel();
}

// Resize logic — drag the left edge to resize the panel
function startResize(event) {
  event.preventDefault();
  const startX = event.clientX;
  const startWidth = aiStore.sidePanelWidth;

  function onMouseMove(e) {
    const delta = startX - e.clientX;
    aiStore.setPanelWidth(Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidth + delta)));
  }

  function onMouseUp() {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

// Keyboard shortcuts
function handleKeyDown(event) {
  // Escape key to close panel
  if (event.key === 'Escape' && aiStore.isSidePanelOpen) {
    closePanel();
  }

  // Alt + A to toggle panel
  if (event.altKey && event.key === 'a' && aiPanelEnabled.value) {
    event.preventDefault();
    togglePanel();
  }
}

// Track panel width as a CSS custom property on :root so global CSS can
// restrict dialog overlays to the content area when the panel is open.
watchEffect(() => {
  const offset = aiStore.isSidePanelOpen ? `${aiStore.sidePanelWidth}px` : '0px';
  document.documentElement.style.setProperty('--ai-panel-offset', offset);
});

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
  document.documentElement.style.setProperty('--ai-panel-offset', '0px');
});
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

// Toggle button — fixed to right edge, visible when panel is closed.
// z-index must exceed Vuetify's dialog overlay (2400) so it remains
// clickable when a dialog is open and the panel is closed.
.ai-side-panel-toggle {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2500;
  pointer-events: all;

  .toggle-btn {
    background: rgba(25, 118, 210, 0.9) !important;
    color: white !important;
    border-radius: 12px 0 0 12px !important;
    min-width: 32px;
    height: 40px;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px 0 6px;

    .toggle-text {
      opacity: 0;
      width: 0;
      overflow: hidden;
      white-space: nowrap;
      margin-left: 0;
      transition: all 0.3s ease;
      font-size: 1.3rem;
      font-weight: 600;
    }

    &:hover {
      background: rgb(25, 118, 210) !important;
      box-shadow: -4px 0 12px rgba(25, 118, 210, 0.3) !important;
      min-width: 110px !important;
      transform: translateX(-4px);

      .toggle-text {
        opacity: 1;
        width: auto;
        margin-left: 6px;
      }
    }
  }
}

// Resize handle — thin draggable strip on the left edge of the drawer
.resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 10;

  &:hover {
    background: rgba(25, 118, 210, 0.2);
  }

  &:active {
    background: rgba(25, 118, 210, 0.4);
  }
}

// Full-height flex column filling the drawer
.ai-side-panel-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-side-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
  background: #ffffff;
  min-height: 64px;
  position: relative;
  z-index: 10;

  .header-content {
    display: flex;
    align-items: center;
    gap: 16px;

    .header-icon {
      font-size: 32px;
    }

    .header-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }

    .header-tags {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: 20px;
    }

    :deep(.ai-tag),
    :deep(.beta-tag) {
      margin: 0 2px;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .pin-btn,
  .close-btn {
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }

  .pin-btn--active {
    opacity: 1;
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.ai-mode-tabs {
  :deep(.v-tabs) {
    --v-tabs-height: 35px;
  }

  :deep(.v-tab) {
    font-size: 13px;
    background-color: $assortment-unselected-tab-colour;
    color: $assortment-inactive-tab-text-colour;
    border: 0.3px solid $assortment-border-colour;
    border-left: 3px solid $assortment-border-colour;
    justify-content: flex-start;
    align-items: center;
    padding: 0 1rem;
    font-weight: bold;
    line-height: 1;
    text-transform: none;

    &:not(:first-child) {
      margin-left: 0.4rem;
    }

    &.v-tab--selected {
      background-color: $assortment-selected-tab-colour;
      color: $assortment-text-colour;
      border-bottom: 1px solid $assortment-selected-tab-colour;
      position: relative;
      z-index: 1;
      margin-bottom: -1px;
    }
  }

  :deep(.v-slide-group__content) {
    border-bottom: 1px solid $assortment-border-colour;
  }
}

.ai-side-panel-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-top: 16px;
}

.mode-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-content-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.no-content-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

// Dark theme adjustments
:deep(.v-theme--dark) {
  .ai-side-panel-header {
    background: rgb(var(--v-theme-surface));
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }
}
</style>
